import { useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { websiteAuthService } from "@/api/websiteAuthService"

export const VerifyEmail = () => {
  const [params] = useSearchParams()
  const verificationPath = params.get("verification_url")
  const queryEmail = params.get("email") || ""
  const [email, setEmail] = useState(queryEmail)
  const [isVerifying, setIsVerifying] = useState(Boolean(verificationPath))
  const [isVerified, setIsVerified] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [message, setMessage] = useState("")
  const [error, setError] = useState("")

  useEffect(() => {
    setEmail(queryEmail)
    setIsVerified(false)
    setMessage("")
    setError("")
    setIsVerifying(Boolean(verificationPath))
    if (!verificationPath) return

    const controller = new AbortController()
    websiteAuthService
      .verifyEmail(verificationPath, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return
        setIsVerified(true)
        setMessage(response.message)
      })
      .catch((error) => {
        if (controller.signal.aborted) return
        setError(
          error?.message ||
            "Unable to verify your email. Please request a new link."
        )
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsVerifying(false)
      })
    return () => controller.abort()
  }, [verificationPath, queryEmail])

  const resend = async (event: React.FormEvent) => {
    event.preventDefault()
    setIsSending(true)
    setMessage("")
    setError("")
    try {
      const response = await websiteAuthService.resendVerification(email.trim())
      setMessage(response.message)
    } catch (error: any) {
      setError(
        error?.error?.email?.[0] ||
          error?.message ||
          "Unable to send a verification email. Please try again."
      )
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-6 bg-white p-8 rounded-xl shadow-md">
        <h1 className="text-3xl font-bold text-gray-900">
          {isVerified ? "Email verified" : "Verify your email"}
        </h1>
        {isVerifying ? (
          <p role="status" className="text-sm text-gray-600">
            Verifying your email...
          </p>
        ) : (
          <>
            {!isVerified && (
              <p className="text-sm text-gray-600">
                Open the verification link in your email to finish creating your
                account. The link expires in 60 minutes. Check your spam folder
                too.
              </p>
            )}
            {message && (
              <p
                role="status"
                className="rounded-md bg-green-50 p-3 text-sm text-green-800"
              >
                {message}
              </p>
            )}
            {error && (
              <p
                role="alert"
                className="rounded-md bg-red-50 p-3 text-sm text-red-700"
              >
                {error}
              </p>
            )}
            {!isVerified && (
              <form onSubmit={resend} className="space-y-4">
                <div>
                  <label
                    htmlFor="verification-email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email address
                  </label>
                  <input
                    id="verification-email"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength={255}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={isSending}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSending}
                  className="w-full rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isSending ? "Sending..." : "Resend verification email"}
                </button>
              </form>
            )}
          </>
        )}
        <Link
          to="/login"
          className="block text-center text-sm text-indigo-600 hover:text-indigo-500"
        >
          {isVerified ? "Sign in" : "Back to login"}
        </Link>
      </div>
    </div>
  )
}
